import { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Trash2, ShoppingBag, Loader2, Phone, MapPin, Info, Truck, Crown, Smartphone } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { formatItemPrice } from '../lib/currency';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Shipping options - dynamic based on cart contents
const getShippingOptions = (isAppointmentOnly, hasNewGear) => {
  if (isAppointmentOnly) {
    return [
      { id: 'collect', name: 'Collect by Appointment', price: 0, description: 'Free - 735 Orkney Cres, Faerie Glen (By appointment only)' },
      { id: 'local', name: 'Local Delivery (Gauteng)', price: 150, description: '2-3 business days' },
      { id: 'national', name: 'National Delivery', price: 250, description: '3-5 business days' },
      { id: 'express', name: 'Express Delivery', price: 450, description: '1-2 business days' },
    ];
  }
  return [
    { id: 'collect', name: 'Collect from Store', price: 0, description: 'Free - Aqua Divers Pretoria or Randburg' },
    { id: 'local', name: 'Local Delivery (Gauteng)', price: 150, description: '2-3 business days' },
    { id: 'national', name: 'National Delivery', price: 250, description: '3-5 business days' },
    { id: 'express', name: 'Express Delivery', price: 450, description: '1-2 business days' },
  ];
};

const CartPage = () => {
  const { cart, removeFromCart, updateQuantity, getCartTotal, clearCart, updateItemSize, updateItemPhoneModel } = useCart();
  const { user, token } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedShipping, setSelectedShipping] = useState('collect');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [phoneModels, setPhoneModels] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState({});
  // Shipping details — pre-filled from the user profile. Required at checkout
  // because we now skip them at signup to lower registration friction.
  const [shipPhone, setShipPhone] = useState('');
  const [shipAddress, setShipAddress] = useState('');
  const [savingDetails, setSavingDetails] = useState(false);

  useEffect(() => {
    if (user) {
      setShipPhone(user.contact_number || '');
      setShipAddress(user.physical_address || '');
    }
  }, [user]);

  const needsShippingDetails = !!user && !user.physical_address;
  
  const membershipPurchase = location.state?.membershipPurchase;

  // Phone model keywords for products that need phone selection
  const PHONE_KEYWORDS = ['seatouch', 'ocean kit', 'wide-angle conversion', 'customized phone adapter', 'underwater housing'];
  const requiresPhoneModel = (name) => {
    const n = (name || '').toLowerCase();
    return PHONE_KEYWORDS.some(kw => n.includes(kw));
  };

  // Load phone models if any cart item needs it
  useEffect(() => {
    const needsPhone = cart.some(item => requiresPhoneModel(item.name));
    if (needsPhone && !phoneModels) {
      axios.get(`${API_URL}/phone-models`).then(res => setPhoneModels(res.data)).catch(() => {});
    }
  }, [cart]);

  // Load the user's active promo credits so we can preview the discount in
  // the cart UI (the actual application happens server-side at /checkout).
  const [promoCredits, setPromoCredits] = useState([]);
  useEffect(() => {
    if (!token) return;
    axios.get(`${API_URL}/me/promo-credits`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setPromoCredits(res.data || []))
      .catch(() => setPromoCredits([]));
  }, [token]);

  // Load admin-configured discount tiers (new gear vs pre-dived, per tier).
  // Backend defaults are 20/25 for new gear and 0/0 for pre-dived if unset.
  const [siteDiscounts, setSiteDiscounts] = useState({});
  useEffect(() => {
    axios.get(`${API_URL}/site-content`)
      .then((r) => {
        const d = r.data || {};
        setSiteDiscounts({
          discount_club_new: Number(d.discount_club_new ?? 20),
          discount_pro_new: Number(d.discount_pro_new ?? 25),
          discount_club_predived: Number(d.discount_club_predived ?? 0),
          discount_pro_predived: Number(d.discount_pro_predived ?? 0),
        });
      })
      .catch(() => {});
  }, []);

  // Separate trip items from regular products
  // Tag the cart by item type. Trips, courses and events are NON-PHYSICAL —
  // they are issued as a Paid Invoice/receipt to present on the day, not
  // shipped or collected like product gear.
  const isNonPhysical = (item) => {
    if (item.isTrip) return true;
    const cat = (item.category || '').toLowerCase();
    return cat === 'courses' || cat === 'events' || cat === 'trips' || cat === 'course' || cat === 'event' || cat === 'trip';
  };
  const tripItems = cart.filter((it) => it.isTrip);
  const nonPhysicalItems = cart.filter(isNonPhysical);
  const productItems = cart.filter((it) => !isNonPhysical(it));
  
  // Categorize cart items for collection address display
  const hasNewGear = cart.some(item => 
    !item.isTrip && 
    item.type !== 'brand_product' && 
    item.category !== 'pre-owned' &&
    !item.isCourse &&
    !item.isEvent
  );
  const hasDiveVolkOrSpecial = cart.some(item => 
    item.type === 'brand_product' || 
    item.isTrip || 
    item.isCourse ||
    item.isEvent ||
    item.category === 'pre-owned'
  );
  const isMixedCart = hasNewGear && hasDiveVolkOrSpecial;
  const isAppointmentOnly = hasDiveVolkOrSpecial && !hasNewGear;
  const isNewGearOnly = hasNewGear && !hasDiveVolkOrSpecial;
  
  // Calculate totals - non-physical items (trips, courses, events) get no shipping.
  const productSubtotal = productItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const tripTotal = tripItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const nonPhysicalTotal = nonPhysicalItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  // Promo credit PREVIEW (mirrors the server-side logic in routes/promo_credits.py)
  // so customers see "Promo applied: −R475" in the cart instead of full price.
  const promoPreview = (() => {
    if (!promoCredits || promoCredits.length === 0) return { discount: 0, applied: [] };
    let discount = 0;
    const applied = [];
    const consumed = new Set();
    for (const credit of promoCredits) {
      if (credit.type === 'free_item') {
        const idx = cart.findIndex((it, i) => !consumed.has(i) && (it.product_id || it.id) === credit.value);
        if (idx >= 0) {
          const it = cart[idx];
          const lineTotal = (it.price || 0) * (it.quantity || 1);
          discount += lineTotal;
          consumed.add(idx);
          applied.push({ reason: credit.reason, amount: lineTotal, item: it.name });
        }
      } else if (credit.type === 'fixed_amount') {
        const amount = Number(credit.value) || 0;
        if (amount <= 0) continue;
        // Pick most expensive remaining item that matches category constraint
        const candidates = cart
          .map((it, i) => ({ it, i, lineTotal: (it.price || 0) * (it.quantity || 1) }))
          .filter(({ i, it }) => {
            if (consumed.has(i)) return false;
            if (!credit.applies_to_category) return true;
            const cat = (it.category || (it.isTrip ? 'trip' : '')).toLowerCase();
            const needle = String(credit.applies_to_category).toLowerCase();
            return cat.includes(needle) || needle.includes(cat);
          })
          .sort((a, b) => b.lineTotal - a.lineTotal);
        if (candidates.length > 0) {
          const target = candidates[0];
          const actual = Math.min(amount, target.lineTotal);
          discount += actual;
          consumed.add(target.i);
          applied.push({ reason: credit.reason, amount: actual, item: target.it.name });
        }
      }
    }
    return { discount, applied };
  })();

  const handleSizeChange = (itemId, size) => {
    if (updateItemSize) {
      updateItemSize(itemId, size);
    }
  };

  const handleCheckout = async () => {
    if (!user) {
      navigate('/auth', { state: { from: '/cart' } });
      return;
    }

    if (cart.length === 0 && !membershipPurchase) {
      toast.error('Your cart is empty');
      return;
    }

    // Check if items with sizes have a size selected
    const itemsNeedingSize = cart.filter(item => item.sizes && item.sizes.length > 0 && !item.selectedSize);
    if (itemsNeedingSize.length > 0) {
      toast.error('Please select a size for all items');
      return;
    }

    // Check if items requiring phone model have one selected
    const itemsNeedingPhone = cart.filter(item => requiresPhoneModel(item.name) && !item.selectedPhoneModel);
    if (itemsNeedingPhone.length > 0) {
      toast.error('Please select your phone model for DiveVolk products');
      return;
    }

    if (!agreedToTerms) {
      toast.error('Please agree to the Terms and Conditions');
      return;
    }

    // If the visitor signed up via the cut-down signup form, they haven't
    // supplied a shipping address yet — collect & persist it here before
    // we attempt to place the order. (Phone is captured at registration.)
    const phoneTrim = (shipPhone || '').trim();
    const addressTrim = (shipAddress || '').trim();
    if (hasPhysicalProducts && selectedShipping !== 'collect' && !addressTrim) {
      toast.error('Please add your shipping address');
      return;
    }
    if (!user?.physical_address) {
      if (hasPhysicalProducts && !addressTrim) {
        toast.error('Please add your shipping address to continue');
        return;
      }
      // Only save the shipping address if the user actually entered one. For
      // courses/events/trips we don't ask for an address at all.
      if (addressTrim) {
        try {
          setSavingDetails(true);
          await axios.put(
            `${API_URL}/user/profile`,
            { contact_number: phoneTrim || undefined, physical_address: addressTrim },
            { headers: { Authorization: `Bearer ${token}` } },
          );
        } catch (e) {
          console.error('Failed to save shipping details', e);
          toast.error('Could not save your shipping address — please try again.');
          setSavingDetails(false);
          return;
        } finally {
          setSavingDetails(false);
        }
      }
    }

    setLoading(true);
    try {
      const items = cart.map(item => {
        // Trip items in the cart have a composite line-item id like
        // "trip-<realTripId>-<timestamp>" (so the same trip can appear
        // multiple times). The backend /checkout endpoint looks up
        // products/trips by their real id — so unwrap it here.
        let productId = item.product_id || item.trip_id || item.tripId;
        if (!productId) {
          const raw = item.id || '';
          const tripMatch = raw.match(/^trip-([0-9a-fA-F-]{36})-\d+$/);
          productId = tripMatch ? tripMatch[1] : raw;
        }
        // For trip cart items, forward the full booking payload so the order
        // (and later the admin bookings screen) preserves attendee list,
        // medical forms, waiver signatures, gear hire, catering, qualifications
        // and last-dive info. TripsPage and TripDetailPage populate slightly
        // different shapes — normalise both here.
        const isTrip = item.type === 'trip' || item.category === 'trip' || item.isTrip || !!item.trip_id || !!item.tripId;
        let booking = null;
        if (isTrip) {
          booking = {
            attendeeCount: item.attendeeCount || item.attendee_count || 1,
            selectedDestination: item.selectedDestination || item.selected_destination || null,
            accommodation: item.accommodation || null,
            pricePerPerson: item.pricePerPerson || null,
            tripPrice: item.tripPrice || null,
            gearHire: item.gearHire || null,
            gearHireCost: item.gearHireCost || 0,
            gearSummary: item.gearSummary || null,
            cateringOpted: item.cateringOpted || false,
            cateringCost: item.cateringCost || 0,
            cateringPricePerPerson: item.cateringPricePerPerson || 0,
            tripDays: item.tripDays || null,
            // Everything the form collected (attendee details, medical Q&A,
            // waiver signatures, qualifications, last dive info, emergency
            // contact, etc.)
            formData: item.bookingDetails || item.booking_data?.formData || item.booking_data || null,
            medicalForms: item.medicalForms || null,
            waiverDetails: item.waiverDetails || null,
            submittedAt: item.submittedAt || null,
          };
        }
        return {
          product_id: productId,
          quantity: item.quantity,
          selected_size: item.selectedSize || null,
          selected_phone_model: item.selectedPhoneModel || null,
          session_index: item.session_index ?? null,
          booking,
        };
      });

      const shippingOption = shippingOptions.find(s => s.id === selectedShipping);

      const response = await axios.post(
        `${API_URL}/checkout`,
        {
          items,
          membership_purchase: membershipPurchase,
          shipping_method: hasPhysicalProducts ? selectedShipping : null,
          shipping_cost: hasPhysicalProducts ? (shippingOption?.price || 0) : 0,
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Clear cart
      clearCart();
      
      // Navigate to payment instructions page
      navigate('/payment-instructions', { state: { orderData: response.data } });
      
    } catch (error) {
      console.error('Checkout failed:', error);
      toast.error(error.response?.data?.detail || 'Checkout failed');
      setLoading(false);
    }
  };

  // Calculate totals - discounts only apply to NEW gear products, not trips
  // and not pre-dived items. Discount tiers are ADMIN-CONFIGURABLE via
  // /api/site-content — see settings tab. Pre-dived defaults to 0%.
  const subtotal = productSubtotal;
  const membershipPrice = membershipPurchase === 'club' ? 1000 : membershipPurchase === 'pro' ? 3600 : 0;

  // Split product subtotal by pre-owned vs new gear.
  const preDivedSubtotal = productItems
    .filter((it) => (it.category || '').toLowerCase() === 'pre-owned')
    .reduce((s, it) => s + it.price * it.quantity, 0);
  const newGearSubtotal = productSubtotal - preDivedSubtotal;

  const newGearDiscountPct = user?.membership_tier === 'pro'
    ? (siteDiscounts.discount_pro_new ?? 25)
    : user?.membership_tier === 'club'
      ? (siteDiscounts.discount_club_new ?? 20)
      : 0;
  const preDivedDiscountPct = user?.membership_tier === 'pro'
    ? (siteDiscounts.discount_pro_predived ?? 0)
    : user?.membership_tier === 'club'
      ? (siteDiscounts.discount_club_predived ?? 0)
      : 0;
  const discountPercent = newGearDiscountPct;  // Shown as the "member discount %" label
  const discountAmount =
    newGearSubtotal * newGearDiscountPct / 100 +
    preDivedSubtotal * preDivedDiscountPct / 100;
  
  // Get shipping options based on cart contents
  const shippingOptions = getShippingOptions(isAppointmentOnly, hasNewGear);
  
  // Shipping only applies when there are physical product items in the cart.
  const hasPhysicalProducts = productItems.length > 0;
  const shippingCost = hasPhysicalProducts ? (shippingOptions.find(s => s.id === selectedShipping)?.price || 0) : 0;
  
  // Total = products (with discount) + trips/courses/events (no discount) + membership + shipping − promos
  const total = Math.max(
    0,
    (productSubtotal - discountAmount) + tripTotal + (nonPhysicalTotal - tripTotal) + membershipPrice + shippingCost - promoPreview.discount,
  );
  
  // Check if user is a member (for discount display purposes only)
  const isMember = user?.membership_tier === 'club' || user?.membership_tier === 'pro';
  const isBuyingMembership = membershipPurchase === 'club' || membershipPurchase === 'pro';
  // Users can checkout if they are logged in - membership is only for discounts, not required for purchase
  const canCheckout = !!user;

  if (cart.length === 0 && !membershipPurchase) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="text-center">
          <ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-4" strokeWidth={1.5} />
          <h2 className="text-2xl font-heading font-semibold text-gray-600 mb-2">
            Your cart is empty
          </h2>
          <p className="text-gray-500 mb-6">Add some gear to get started</p>
          <Button 
            onClick={() => navigate('/products')}
            className="bg-[#0A2342] text-white hover:bg-[#0A2342]/90"
            data-testid="empty-cart-shop-btn"
          >
            Shop Products
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 bg-[#FAFAFA]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <h1 className="text-4xl font-heading font-bold text-[#0A2342] mb-8">Shopping Cart</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4">
            
            {/* Dynamic Collection Address Notice based on cart contents */}
            {isMixedCart ? (
              /* Mixed Cart - Contact Admin */
              <div className="bg-amber-500 text-white p-5 rounded-lg">
                <div className="flex items-start gap-3">
                  <Info className="w-6 h-6 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-bold text-lg mb-2">Mixed Order - Contact Admin</h4>
                    <p className="mb-3">
                      Your cart contains items that require collection from different locations. Please contact us to arrange your order.
                    </p>
                    <div className="bg-white/20 p-3 rounded-lg">
                      <p className="font-semibold">Contact: +2782 579 3732</p>
                      <p className="text-sm">Email: info@justdive.co.za</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : isAppointmentOnly ? (
              /* DiveVolk, Trips, Courses, Pre-Dived - Appointment Only */
              <div className="bg-[#FF6B35] text-white p-5 rounded-lg">
                <div className="flex items-start gap-3">
                  <MapPin className="w-6 h-6 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-bold text-lg mb-1">Collection Address</h4>
                    <p className="text-lg font-medium mb-3">735 Orkney Cres, Faerie Glen, Pretoria</p>
                    <div className="bg-white text-[#0A2342] p-4 rounded-lg text-center">
                      <p className="font-bold text-xl mb-1">BY APPOINTMENT ONLY</p>
                      <p className="text-lg">
                        For appointments call <a href="tel:+27825793732" className="text-[#FF6B35] font-bold hover:underline">+2782 579 3732</a>
                      </p>
                    </div>
                    <p className="text-sm mt-3 opacity-90">
                      * Applies to DiveVolk products, trips, courses, and pre-dived gear
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              /* New Gear Only - Aqua Divers Addresses */
              <div className="bg-[#0A2342] text-white p-4 rounded-lg flex items-start gap-3">
                <Phone className="w-5 h-5 text-[#FF6B35] flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold mb-1">Collection & Sizing Help</h4>
                  <p className="text-sm text-gray-300 mb-2">
                    Contact Aqua Divers for confirmation of size and stock availability before ordering.
                  </p>
                  <div className="mt-3 space-y-3">
                    <div className="bg-white/10 p-3 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <MapPin className="w-4 h-4 text-[#FF6B35]" />
                        <span className="font-medium">Pretoria Store</span>
                      </div>
                      <p className="text-sm ml-6">Shop G145 Parkview Shopping Centre, 24 Garsfontein Rd</p>
                      <a href="tel:+27128805300" className="text-[#FF6B35] ml-6 text-sm hover:underline">+2712 880 5300</a>
                    </div>
                    <div className="bg-white/10 p-3 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <MapPin className="w-4 h-4 text-[#FF6B35]" />
                        <span className="font-medium">Randburg Store</span>
                      </div>
                      <p className="text-sm ml-6">Unit C5, Northland Deco Park, Design Blvd, Hoogland</p>
                      <a href="tel:+27117949444" className="text-[#FF6B35] ml-6 text-sm hover:underline">+2711 794 9444</a>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {cart.map((item) => (
              <div
                key={item.id}
                className="bg-white p-4 sm:p-6 rounded-lg border border-gray-100"
                data-testid={`cart-item-${item.id}`}
              >
                {/* Mobile layout: stacked, Desktop: side by side */}
                <div className="flex flex-col sm:flex-row gap-4">
                  {/* Image and basic info row on mobile */}
                  <div className="flex gap-4">
                    <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gray-100 rounded-lg flex-shrink-0 overflow-hidden">
                      <img
                        src={item.image_url}
                        alt={item.name}
                        className="w-full h-full object-contain"
                      />
                    </div>
                    
                    {/* Name and price side by side on mobile */}
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start gap-2">
                        <h3 className="text-base sm:text-lg font-heading font-semibold text-[#0A2342] leading-tight">
                          {item.name}
                        </h3>
                        {/* Price visible on mobile at top right */}
                        <span className="text-lg sm:hidden font-mono-accent font-bold text-[#0A2342] whitespace-nowrap">
                          {formatItemPrice(item.price * item.quantity, item.currency)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2 hidden sm:block">
                        {item.description}
                      </p>
                      {/* Display selected color if any */}
                      {item.selectedColor && (
                        <div className="mt-1 text-sm text-gray-600">
                          <span className="font-medium">Color:</span> {item.selectedColor}
                        </div>
                      )}
                      {/* Display selected size if set from product detail */}
                      {item.selectedSize && !item.sizes?.length && (
                        <div className="mt-1 text-sm text-gray-600">
                          <span className="font-medium">Size:</span> {item.selectedSize}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Controls row */}
                  <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 sm:ml-0">
                    <div className="flex-1">
                      {/* Size selector */}
                      {item.sizes && item.sizes.length > 0 && (
                        <div className="mb-3">
                          <label className="text-sm font-medium text-gray-700 mb-1 block">Size:</label>
                          <Select
                            value={item.selectedSize || ''}
                            onValueChange={(value) => handleSizeChange(item.id, value)}
                          >
                            <SelectTrigger className="w-full sm:w-48" data-testid={`size-select-${item.id}`}>
                              <SelectValue placeholder="Select size" />
                            </SelectTrigger>
                            <SelectContent>
                              {item.sizes.map((size) => (
                                <SelectItem key={size} value={size}>
                                  {size}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      
                      {/* Phone Model Selector for DiveVolk products */}
                      {requiresPhoneModel(item.name) && phoneModels && (
                        <div className="mb-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                          <label className="text-sm font-medium text-[#0A2342] mb-2 block flex items-center gap-1">
                            <Smartphone className="w-4 h-4" /> Select Your Phone Model *
                          </label>
                          <div className="flex flex-col gap-2">
                            <Select
                              value={selectedBrand[item.id] || ''}
                              onValueChange={(value) => {
                                setSelectedBrand(prev => ({ ...prev, [item.id]: value }));
                                updateItemPhoneModel(item.id, null);
                              }}
                            >
                              <SelectTrigger className="w-full" data-testid={`phone-brand-${item.id}`}>
                                <SelectValue placeholder="Select Phone Brand" />
                              </SelectTrigger>
                              <SelectContent>
                                {phoneModels.brand_list.map(brand => (
                                  <SelectItem key={brand} value={brand}>{brand}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Select
                              value={item.selectedPhoneModel || ''}
                              onValueChange={(value) => updateItemPhoneModel(item.id, value)}
                              disabled={!selectedBrand[item.id]}
                            >
                              <SelectTrigger className="w-full" data-testid={`phone-model-${item.id}`}>
                                <SelectValue placeholder={selectedBrand[item.id] ? "Select Model" : "Select brand first"} />
                              </SelectTrigger>
                              <SelectContent>
                                {(phoneModels.brands[selectedBrand[item.id]] || []).map(model => (
                                  <SelectItem key={model} value={`${selectedBrand[item.id]} ${model}`}>
                                    {model}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          {item.selectedPhoneModel && (
                            <p className="text-xs text-green-600 mt-2 font-medium">Selected: {item.selectedPhoneModel}</p>
                          )}
                        </div>
                      )}

                      {/* Quantity controls */}
                      <div className="flex items-center justify-between sm:justify-start gap-3">
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            className="h-8 w-8 p-0 flex items-center justify-center"
                            data-testid={`decrease-qty-${item.id}`}
                          >
                            -
                          </Button>
                          <span className="font-mono-accent font-semibold w-10 text-center" data-testid={`qty-${item.id}`}>
                            {item.quantity}
                          </span>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            className="h-8 w-8 p-0 flex items-center justify-center"
                            data-testid={`increase-qty-${item.id}`}
                          >
                            +
                          </Button>
                        </div>
                        
                        {/* Remove button - visible on mobile in same row */}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => removeFromCart(item.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 px-2 sm:hidden"
                          data-testid={`remove-mobile-${item.id}`}
                        >
                          <Trash2 className="w-4 h-4 mr-1" strokeWidth={1.5} />
                          <span className="text-sm">Remove</span>
                        </Button>
                      </div>
                    </div>
                    
                    {/* Price and remove - desktop only column */}
                    <div className="hidden sm:flex flex-col items-end justify-between h-full ml-4">
                      <span className="text-xl font-mono-accent font-bold text-[#0A2342]">
                        {formatItemPrice(item.price * item.quantity, item.currency)}
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeFromCart(item.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0 mt-2"
                        data-testid={`remove-${item.id}`}
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {membershipPurchase && (
              <div className="bg-[#F0EFEA] p-6 rounded-lg border-2 border-[#FF6B35]">
                <h3 className="text-lg font-heading font-semibold text-[#0A2342] mb-2">
                  {membershipPurchase === 'club' ? 'Just Dive Club' : 'Just Dive Pro'} Membership
                </h3>
                <p className="text-sm text-gray-600 mb-2">
                  {membershipPurchase === 'club' 
                    ? 'Annual membership with 20% discount on all products' 
                    : '12-month membership with 25% discount on all products'}
                </p>
                {membershipPurchase === 'pro' && (
                  <p className="text-xs text-gray-500 mb-2">
                    R300/month × 12 months (paid upfront)
                  </p>
                )}
                <span className="text-xl font-mono-accent font-bold text-[#0A2342]">
                  R{membershipPrice.toLocaleString()}
                </span>
              </div>
            )}

            {/* Shipping Options - Only show if there are physical products */}
            {hasPhysicalProducts && (
              <div className="bg-white p-6 rounded-lg border border-gray-100">
                <div className="flex items-center gap-2 mb-4">
                  <Truck className="w-5 h-5 text-[#0A2342]" />
                  <h3 className="text-lg font-heading font-semibold text-[#0A2342]">
                    Shipping Method
                  </h3>
                </div>
                <div className="space-y-3">
                  {shippingOptions.map((option) => (
                    <label
                      key={option.id}
                      className={`flex items-center justify-between p-4 border rounded-lg cursor-pointer transition-all ${
                        selectedShipping === option.id
                          ? 'border-[#FF6B35] bg-[#FF6B35]/5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          name="shipping"
                          value={option.id}
                          checked={selectedShipping === option.id}
                          onChange={(e) => setSelectedShipping(e.target.value)}
                          className="w-4 h-4 text-[#FF6B35]"
                        />
                        <div>
                          <p className="font-medium text-[#0A2342]">{option.name}</p>
                          <p className="text-sm text-gray-500">{option.description}</p>
                        </div>
                      </div>
                      <span className="font-mono-accent font-semibold text-[#0A2342]">
                        {option.price === 0 ? 'FREE' : `R${option.price.toFixed(2)}`}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

            <div className="lg:col-span-1">
            <div className="bg-white p-6 rounded-lg border border-gray-100 shadow-sm sticky top-24">
              <h3 className="text-xl font-heading font-semibold text-[#0A2342] mb-6">
                Order Summary
              </h3>
              
              <div className="space-y-3 mb-6">
                {productSubtotal > 0 && (
                  <div className="flex justify-between text-gray-600">
                    <span>Products Subtotal</span>
                    <span className="font-mono-accent">R{productSubtotal.toFixed(2)}</span>
                  </div>
                )}
                
                {tripTotal > 0 && (
                  <div className="flex justify-between text-gray-600">
                    <span>Trip Bookings</span>
                    <span className="font-mono-accent">R{tripTotal.toFixed(2)}</span>
                  </div>
                )}

                {(nonPhysicalTotal - tripTotal) > 0 && (
                  <div className="flex justify-between text-gray-600">
                    <span>Courses / Events</span>
                    <span className="font-mono-accent">R{(nonPhysicalTotal - tripTotal).toFixed(2)}</span>
                  </div>
                )}
                
                {membershipPrice > 0 && (
                  <div className="flex justify-between text-gray-600">
                    <span>Membership</span>
                    <span className="font-mono-accent">R{membershipPrice.toFixed(2)}</span>
                  </div>
                )}
                
                {discountAmount > 0 && (
                  <div className="flex justify-between text-[#FF6B35]">
                    <span>Member Discount{newGearSubtotal > 0 ? ` (${discountPercent}% on new gear)` : ''}</span>
                    <span className="font-mono-accent">-R{discountAmount.toFixed(2)}</span>
                  </div>
                )}

                {promoPreview.discount > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded p-2" data-testid="promo-applied-block">
                    <div className="flex justify-between text-green-700 font-semibold">
                      <span>🎁 Promo Applied</span>
                      <span className="font-mono-accent">-R{promoPreview.discount.toFixed(2)}</span>
                    </div>
                    {promoPreview.applied.map((a, i) => (
                      <div key={i} className="text-xs text-green-700 mt-0.5">
                        {a.reason || 'Promo credit'} ({a.item})
                      </div>
                    ))}
                  </div>
                )}

                {hasPhysicalProducts && (
                  <div className="flex justify-between text-gray-600">
                    <span>Shipping</span>
                    <span className="font-mono-accent">
                      {shippingCost === 0 ? 'FREE' : `R${shippingCost.toFixed(2)}`}
                    </span>
                  </div>
                )}

                {nonPhysicalItems.length > 0 && !hasPhysicalProducts && (
                  <div className="text-xs text-gray-500 italic bg-blue-50 border border-blue-100 rounded p-2" data-testid="non-physical-info">
                    📄 You'll receive a paid invoice/receipt by email to present on the day. Contact us on <a href="tel:+27675355405" className="text-[#FF6B35] hover:underline font-medium">+27 67 535 5405</a> for any inquiries.
                  </div>
                )}

                {tripTotal > 0 && hasPhysicalProducts && (
                  <div className="text-xs text-gray-500 italic">
                    * Trip bookings: No shipping or discounts apply
                  </div>
                )}
                
                <div className="border-t border-gray-200 pt-3">
                  <div className="flex justify-between text-lg font-semibold">
                    <span className="text-[#0A2342]">Total</span>
                    <span className="font-mono-accent text-[#0A2342]">R{total.toFixed(2)}</span>
                  </div>
                  {cart.some((it) => it.currency && it.currency !== 'ZAR') && (
                    <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-2" data-testid="cart-foreign-currency-note">
                      Some items are priced in a foreign currency. Checkout settles in ZAR — totals above are computed using the displayed numbers as ZAR. Please contact us to confirm settlement for foreign-priced items.
                    </div>
                  )}
                </div>
              </div>

              {/* Membership Promotion Notice - Only show if user is not a member */}
              {user && !isMember && !isBuyingMembership && (
                <div className="mb-6 p-4 bg-[#0A2342]/5 border border-[#0A2342]/20 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Crown className="w-5 h-5 text-[#FF6B35] flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-[#0A2342] mb-1">Save with Membership!</h4>
                      <p className="text-sm text-gray-600 mb-3">
                        Join Just Dive Club (20% off) or Pro (25% off) to enjoy exclusive member discounts on all products.
                      </p>
                      <Link to="/membership">
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-[#FF6B35] border-[#FF6B35] hover:bg-[#FF6B35]/10"
                          data-testid="join-membership-btn"
                        >
                          View Membership Options
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              )}

              {/* Shipping address — collected here for users who skipped it at signup. */}
              {user && needsShippingDetails && (
                <div className="mb-6 p-4 bg-[#FFF3ED] border border-[#FF6B35]/30 rounded-lg" data-testid="cart-shipping-details-block">
                  <div className="flex items-start gap-2 mb-3">
                    <MapPin className="w-4 h-4 text-[#FF6B35] mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-[#0A2342]">Add your shipping address</p>
                      <p className="text-xs text-gray-600">One-time step — we'll remember it for next time.</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {!user.contact_number && (
                      <div>
                        <label className="text-xs font-medium text-gray-700">Contact number</label>
                        <input
                          type="tel"
                          value={shipPhone}
                          onChange={(e) => setShipPhone(e.target.value)}
                          placeholder="0821234567"
                          className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/30 focus:border-[#FF6B35]"
                          data-testid="cart-shipping-phone"
                        />
                      </div>
                    )}
                    <div>
                      <label className="text-xs font-medium text-gray-700">Shipping address</label>
                      <input
                        type="text"
                        value={shipAddress}
                        onChange={(e) => setShipAddress(e.target.value)}
                        placeholder="123 Main Street, City, Province, Postal Code"
                        className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/30 focus:border-[#FF6B35]"
                        data-testid="cart-shipping-address"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Terms checkbox */}
              <div className="mb-6">
                <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors">
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="w-5 h-5 text-[#FF6B35] rounded border-gray-300 focus:ring-[#FF6B35]"
                    disabled={!user}
                  />
                  <span className="text-sm text-gray-600">
                    I agree to the{' '}
                    <Link to="/terms" className="text-[#FF6B35] hover:underline font-medium" target="_blank">
                      Terms and Conditions
                    </Link>
                  </span>
                </label>
              </div>

              <Button
                onClick={handleCheckout}
                disabled={loading || !agreedToTerms || !user}
                className="w-full bg-[#FF6B35] text-white hover:bg-[#FF6B35]/90 shadow-lg hover:shadow-xl transition-all duration-300 h-12 text-base font-semibold disabled:opacity-50"
                data-testid="checkout-btn"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Processing...
                  </span>
                ) : !user ? (
                  'Sign In to Checkout'
                ) : (
                  'Proceed to Checkout'
                )}
              </Button>

              {!user && (
                <p className="text-sm text-center text-gray-500 mt-3">
                  Sign in to complete your purchase
                </p>
              )}

              <div className="mt-6 pt-4 border-t border-gray-100">
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <Info className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-gray-500">
                    Payment is via EFT. You'll receive banking details after checkout. 
                    Shipping commences after payment confirmation.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
